import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Radio, Divider, Select } from 'antd';
import { FormattedMessage } from 'react-intl';
import config from '../../config';
import { intents, variants } from '../styled-antd/utils';
import Price from './Price';
import Features from '../../public/UI/Features';
import PlanName from './PlanName';
import CurrencyName from './CurrencyName';
import PlanInterval from './PlanInterval';
import { StyledButton } from '../styled-antd';

const ChoosePlanInterval = ({ value: defaultPlan, onChange, withFeatures, plans }) => {
  const selectedPlanObj = useMemo(
    () => plans.find((p) => p.id === defaultPlan),
    [defaultPlan, plans]
  );
  const plansByCurrency = useMemo(
    () =>
      plans
        .filter((p) => p.currency === selectedPlanObj?.currency)
        .sort((a, b) => {
          if (a.interval === 'year' && b.interval === 'month') {
            return -1;
          }
          if (a.interval === 'month' && b.interval === 'year') {
            return 1;
          }
          return 0;
        }),
    [plans, selectedPlanObj?.currency]
  );

  const currencies = useMemo(
    () =>
      plans.reduce((acc, p) => {
        if (!acc.includes(p.currency)) {
          acc.push(p.currency);
        }
        return acc;
      }, []),
    [plans]
  );

  return (
    <div className="join">
      <Row justify="center" align="middle" className="join--title">
        <Col span={18} className="no-padding">
          <h2 className={selectedPlanObj.isFreePlan ? 'freePlanTitle' : ''}>
            {selectedPlanObj.isFreePlan ? (
              <FormattedMessage defaultMessage="STARTER" />
            ) : (
              <PlanName
                plan={selectedPlanObj}
                planType={selectedPlanObj?.planType || defaultPlan}
              />
            )}
          </h2>
        </Col>
        <Col span={6}>
          <StyledButton
            variant={variants.text}
            intent={intents.primary}
            link={`${config.corporateUrl}/pricing`}
          >
            <FormattedMessage defaultMessage="Change plan" />
          </StyledButton>
        </Col>
      </Row>

      {selectedPlanObj?.isFreePlan && (
        <div className="join--subtitle">
          <FormattedMessage defaultMessage="All the essential tools, whether you're new to podcasting or looking to move your show. No strings attached." />
        </div>
      )}

      {withFeatures && <Features planType={selectedPlanObj?.planType || defaultPlan} />}

      {withFeatures && !selectedPlanObj?.isFreePlan && !selectedPlanObj?.isCreatorNetwork && (
        <Divider />
      )}

      {selectedPlanObj && !selectedPlanObj?.isFreePlan && !selectedPlanObj?.isCreatorNetwork && (
        <div className="gutter-top-l gutter-top-lg">
          <Radio.Group
            value={defaultPlan}
            size="large"
            style={{ width: '100%' }}
            onChange={({ target: { value } }) => onChange(value)}
          >
            <>
              {plansByCurrency.map((p) => (
                <Radio.Button
                  key={p.id}
                  value={p.id}
                  style={{
                    width:
                      plansByCurrency?.length > 0 ? `${100 / plansByCurrency.length}%` : '100%',
                    textAlign: 'center',
                  }}
                >
                  <PlanInterval planInterval={p.interval} />
                </Radio.Button>
              ))}
            </>
          </Radio.Group>

          <Row
            className="gutter-top-lg gutter-top-l join--price"
            type="flex"
            align="middle"
            justify="space-between"
          >
            <Col>
              {selectedPlanObj.interval === 'year' && (
                <FormattedMessage
                  defaultMessage="{price} <small>per month, billed yearly</small>"
                  values={{
                    price: (
                      <h2 style={{ marginBottom: '0' }}>
                        <Price
                          amount={selectedPlanObj.cmsPrice / 12}
                          currency={selectedPlanObj.currency}
                        />
                      </h2>
                    ),
                    small: function renderSmall(chunk) {
                      return <small>{chunk}</small>;
                    },
                  }}
                />
              )}

              {selectedPlanObj.interval === 'month' && (
                <FormattedMessage
                  defaultMessage="{price} <small>per month, billed monthly</small>"
                  values={{
                    price: (
                      <h2 style={{ marginBottom: '0' }}>
                        <Price
                          amount={selectedPlanObj.cmsPrice}
                          currency={selectedPlanObj.currency}
                        />
                      </h2>
                    ),
                    small: function renderSmall(chunk) {
                      return <small>{chunk}</small>;
                    },
                  }}
                />
              )}
            </Col>
            <Col>
              <Select
                onChange={(value) => {
                  onChange(plans.find((p) => p.currency === value).id);
                }}
                defaultValue={selectedPlanObj.currency}
                options={currencies.map((c) => ({
                  value: c,
                  label: <CurrencyName currency={c} />,
                }))}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

ChoosePlanInterval.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  withFeatures: PropTypes.bool,
};

export default ChoosePlanInterval;
